import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import {
  PATH_ORGANIZATION_BANK,
  PATH_ORGANIZATION_BANK_EDIT,
} from '../../app/constants/paths.constant';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { OrganizationForm } from '../../forms/ThirdParty/Organization';
import { BankInfo as BankInfoCmp } from '../../components/BankInfo';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const OrganizationPage_BankInfo_Organization_Fragment = graphql(`
  fragment OrganizationPage_BankInfo_Organization_Fragment on Organization {
    _id
    isInternal
    bankInfo {
      accountOwner
      bankCode
      branchCode
      accountNumber
      certificateKey
      iban
      swift
      domiciliation
      ...BankInfoCmp_BankInfo_Fragment
    }
    ...ThirdPartyForm_Organization_Fragment
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_BankInfo_Organization_Fragment
  >;
}

export const BankInfo = ({ organization: pOrganization }: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationPage_BankInfo_Organization_Fragment,
    pOrganization,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_ORGANIZATION_BANK,
      edit: PATH_ORGANIZATION_BANK_EDIT,
    },
    {
      id: organization._id,
    },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <BankInfoCmp bankInfo={organization.bankInfo} />
      <SpeedDial
        actions={[
          {
            id: 'editOrganizationBankInfo',
            icon: <EditIcon />,
            name: 'Modifier Infos Bancaires',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Bancaires de l'Organisation"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <OrganizationForm
          formVariant={FormVariant.Modification}
          initialValues={organization}
          isInternal={organization.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          organizationId={organization._id}
          stepIdsSubset={[ThirdPartyFormTab.BankInfo]}
          title="Formulaire de modification des Informations Bancaires de l'Organisation"
        />
      </RightDrawer>
    </>
  );
};
